import { getChartPalette } from "./chartPalette";
import { estimateBarSpacing } from "./chartOverlayCanvas";

export const SESSION_OVERLAY_EMPTY = { bands: [], dividers: [] };

const SESSION_OVERNIGHT = "overnight";
const SESSION_PREMARKET = "premarket";
const SESSION_REGULAR = "regular";
const SESSION_AFTERHOURS = "afterhours";
const US_EQUITY_MARKET_TIME_ZONE = "America/New_York";
const US_EQUITY_SESSION_ASSET_TYPES = new Set(["stocks", "funds"]);
const SHADED_SESSION_TYPES = new Set([
  SESSION_OVERNIGHT,
  SESSION_PREMARKET,
  SESSION_AFTERHOURS,
]);
const SESSION_DIVIDER_KIND_BY_TYPE = {
  [SESSION_OVERNIGHT]: "overnight-start",
  [SESSION_PREMARKET]: "premarket-start",
  [SESSION_REGULAR]: "market-open",
  [SESSION_AFTERHOURS]: "market-close",
};

export const INTRADAY_SESSION_SCALES = new Set([
  "1m",
  "2m",
  "3m",
  "5m",
  "10m",
  "15m",
  "30m",
  "1h",
  "2h",
  "3h",
  "4h",
]);

const US_EQUITY_PREMARKET_START_MINUTES = 4 * 60;
const US_EQUITY_REGULAR_START_MINUTES = 9 * 60 + 30;
const US_EQUITY_REGULAR_END_MINUTES = 16 * 60;
const US_EQUITY_AFTERHOURS_END_MINUTES = 20 * 60;
const zonedDateTimeFormatterCache = new Map();

const normalizeAssetType = (assetType) =>
  String(assetType || "")
    .trim()
    .toLowerCase();

const shouldShowUsEquitySessions = ({ assetType, scale }) =>
  US_EQUITY_SESSION_ASSET_TYPES.has(normalizeAssetType(assetType)) &&
  INTRADAY_SESSION_SCALES.has(scale);

const getZonedDateTimeFormatter = (timeZone) => {
  if (!zonedDateTimeFormatterCache.has(timeZone)) {
    zonedDateTimeFormatterCache.set(
      timeZone,
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  }

  return zonedDateTimeFormatterCache.get(timeZone);
};

const getZonedDateTimeParts = (absoluteTime, timeZone) => {
  if (!Number.isFinite(absoluteTime)) return null;

  const date = new Date(absoluteTime * 1000);
  if (Number.isNaN(date.getTime())) return null;

  const parts = getZonedDateTimeFormatter(timeZone).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const weekday = get("weekday");
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));

  if (
    !weekday ||
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  return {
    weekday,
    year,
    month,
    day,
    hour,
    minute,
  };
};

const getUsEquitySessionTypeFromParts = (parts) => {
  if (parts.weekday === "Sat" || parts.weekday === "Sun") return null;

  const minutes = parts.hour * 60 + parts.minute;
  if (
    minutes < US_EQUITY_PREMARKET_START_MINUTES ||
    minutes >= US_EQUITY_AFTERHOURS_END_MINUTES
  ) {
    return SESSION_OVERNIGHT;
  }
  if (minutes < US_EQUITY_REGULAR_START_MINUTES) {
    return SESSION_PREMARKET;
  }
  if (minutes < US_EQUITY_REGULAR_END_MINUTES) {
    return SESSION_REGULAR;
  }
  return SESSION_AFTERHOURS;
};

const formatSessionDateKey = ({ year, month, day }) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const nextSessionDateKey = ({ year, month, day }) => {
  const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  date.setUTCDate(date.getUTCDate() + 1);
  return formatSessionDateKey({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
};

const getUsEquitySessionInfo = (absoluteTime) => {
  const parts = getZonedDateTimeParts(
    absoluteTime,
    US_EQUITY_MARKET_TIME_ZONE
  );
  if (!parts) return null;

  const type = getUsEquitySessionTypeFromParts(parts);
  if (!type) return null;

  const minutes = parts.hour * 60 + parts.minute;
  const sessionDateKey =
    type === SESSION_OVERNIGHT && minutes >= US_EQUITY_AFTERHOURS_END_MINUTES
      ? nextSessionDateKey(parts)
      : formatSessionDateKey(parts);

  return {
    type,
    key: `${type}:${sessionDateKey}`,
  };
};

export const buildSessionOverlayConfig = ({ candleData, assetType, scale }) => {
  if (
    !shouldShowUsEquitySessions({ assetType, scale }) ||
    !Array.isArray(candleData) ||
    candleData.length === 0
  ) {
    return SESSION_OVERLAY_EMPTY;
  }

  const sessionPoints = candleData
    .map((entry) => {
      const time = Number(entry?.time);
      const absoluteTime = Number(entry?.absoluteTime);
      if (!Number.isFinite(time) || !Number.isFinite(absoluteTime)) return null;

      const sessionInfo = getUsEquitySessionInfo(absoluteTime);
      if (!sessionInfo) return null;

      return { time, ...sessionInfo };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);

  const bands = [];
  const dividers = [];
  const segments = [];
  let currentSegment = null;

  const commitCurrentSegment = () => {
    if (!currentSegment) return;
    segments.push(currentSegment);
    currentSegment = null;
  };

  sessionPoints.forEach((point) => {
    if (
      !currentSegment ||
      currentSegment.type !== point.type ||
      currentSegment.key !== point.key
    ) {
      if (currentSegment) {
        const kind = SESSION_DIVIDER_KIND_BY_TYPE[point.type];
        if (kind) {
          dividers.push({ time: point.time, kind });
        }
        commitCurrentSegment();
      }

      currentSegment = {
        type: point.type,
        key: point.key,
        startTime: point.time,
        endTime: point.time,
      };
      return;
    }

    currentSegment.endTime = Math.max(currentSegment.endTime, point.time);
  });
  commitCurrentSegment();

  segments.forEach((segment) => {
    if (SHADED_SESSION_TYPES.has(segment.type)) {
      bands.push({
        startTime: segment.startTime,
        endTime: segment.endTime,
        type: segment.type,
      });
    }
  });

  if (bands.length === 0 && dividers.length === 0) {
    return SESSION_OVERLAY_EMPTY;
  }

  return {
    bands: bands.sort((a, b) => a.startTime - b.startTime),
    dividers: dividers.sort((a, b) => a.time - b.time),
  };
};

export const drawSessionOverlay = ({ chart, overlay, context, width, height }) => {
  if (
    !chart ||
    !context ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    (!Array.isArray(overlay?.bands) && !Array.isArray(overlay?.dividers))
  ) {
    return;
  }

  const palette = getChartPalette();
  const timeScale = chart.timeScale?.();
  if (!timeScale) return;

  const barSpacing = estimateBarSpacing(chart, width);
  const halfBar = Math.max(barSpacing / 2, 3);

  (overlay?.bands || []).forEach((band) => {
    if (
      !band ||
      !Number.isFinite(band.startTime) ||
      !Number.isFinite(band.endTime)
    ) {
      return;
    }

    const startCoordinate = timeScale.timeToCoordinate?.(band.startTime);
    const endCoordinate = timeScale.timeToCoordinate?.(band.endTime);
    if (!Number.isFinite(startCoordinate) || !Number.isFinite(endCoordinate)) {
      return;
    }

    const left = Math.max(0, Math.min(startCoordinate, endCoordinate) - halfBar);
    const right = Math.min(width, Math.max(startCoordinate, endCoordinate) + halfBar);
    if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) {
      return;
    }

    context.save();
    context.fillStyle = {
      [SESSION_OVERNIGHT]: palette.overnightFill,
      [SESSION_PREMARKET]: palette.premarketFill,
      [SESSION_AFTERHOURS]: palette.afterhoursFill,
    }[band.type] || palette.premarketFill;
    context.fillRect(left, 0, right - left, height);
    context.restore();
  });

  (overlay?.dividers || []).forEach((divider) => {
    if (!divider || !Number.isFinite(divider.time)) return;

    const x = timeScale.timeToCoordinate?.(divider.time);
    if (!Number.isFinite(x) || x < -2 || x > width + 2) return;

    context.save();
    context.strokeStyle = palette.sessionDivider;
    context.lineWidth = 1;
    context.setLineDash([4, 4]);
    context.beginPath();
    context.moveTo(Math.round(x) + 0.5, 0);
    context.lineTo(Math.round(x) + 0.5, height);
    context.stroke();
    context.restore();
  });
};
